import { ComponentProps, forwardRef, memo, MouseEvent, Ref, useCallback } from 'react'
import { useBlurActiveInputElement, useFormContext } from 'hooks/form'
import { trackError } from 'services/analytics'
import SubmitButton from './SubmitButton'

type OmittedProps = 'validating' | 'submitting' | 'submitted'
type FormSubmitButtonProps = Omit<ComponentProps<typeof SubmitButton>, OmittedProps>

function FormSubmitButton({ disabled, onClick, ...props }: FormSubmitButtonProps, ref: Ref<HTMLButtonElement>) {
  const form = useFormContext()

  if (!form) {
    if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
      throw new Error('FormSubmitButton found no FormProvider.')
    } else {
      trackError(new Error('FormSubmitButton found no FormProvider.'))
    }
  }

  const blurActiveInputElement = useBlurActiveInputElement()

  const onClickWrapped = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    blurActiveInputElement()

    if (onClick) {
      onClick(e)
    } else {
      form.submit(e)
    }
  }, [form, onClick, blurActiveInputElement])

  return (
    <SubmitButton
      submitting={form.formState.isSubmitting}
      validating={form.formState.isValidating}
      submitted={form.formState.isSubmitSuccessful}
      ref={ref}
      {...props}
      disabled={form.disableSubmit || disabled}
      onClick={onClickWrapped}
    />
  )
}

export default memo(forwardRef(FormSubmitButton))
